import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { KPI } from '../api';
import { END_YEAR_STORAGE_KEY, START_YEAR_STORAGE_KEY } from './constants';
import { I18nService } from './i18n.service';
import { map } from 'rxjs/operators';
import * as R from 'ramda';

/**
 * Service that tracks selected start and end year through route query params.
 */
@Injectable()
export class YearsService {

  private currentYear = new Date().getFullYear();
  private defaultEndYear = new Date().getFullYear() + 7;
  private _startYear$ = new BehaviorSubject<number>(null);
  private cachedStartYearIsLoaded: boolean = false;
  private _endYear$ = new BehaviorSubject<number>(null);
  private cachedEndYearIsLoaded: boolean = false;

  public constructor(
    private translate: TranslateService,
    private i18n: I18nService,
  ) { }

  public get startYear$(): Observable<number> {
    if (!this.cachedStartYearIsLoaded) {
      const cachedFilter = localStorage.getItem(START_YEAR_STORAGE_KEY);
      if (cachedFilter) {
        this._startYear$.next(Number(cachedFilter));
      } else {
        this._startYear$.next(this.currentYear);
      }
      this.cachedStartYearIsLoaded = true;
    }
    return this._startYear$;
  }

  public setStartYear(year: number): void {
    if (year !== this._startYear$.value) {
      this._startYear$.next(year);
      localStorage.setItem(START_YEAR_STORAGE_KEY, year.toString());
    }
  }

  public get endYear$(): Observable<number> {
    if (!this.cachedEndYearIsLoaded) {
      const cachedFilter = localStorage.getItem(END_YEAR_STORAGE_KEY);
      if (cachedFilter) {
        this._endYear$.next(Number(cachedFilter));
      } else {
        this._endYear$.next(this.defaultEndYear);
      }
      this.cachedEndYearIsLoaded = true;
    }
    return this._endYear$;
  }

  public setEndYear(year: number): void {
    if (year !== this._endYear$.value) {
      this._endYear$.next(year);
      localStorage.setItem(END_YEAR_STORAGE_KEY, year.toString());
    }
  }

  public get latestYears$(): Observable<[number, number]> {
    return combineLatest([
      this.startYear$,
      this.endYear$,
    ]);
  }

  public get yearsRange$(): Observable<number[]> {
    return combineLatest([
      this.startYear$,
      this.endYear$,
    ]).pipe(
      map(([startYear, endYear]) => R.range(startYear, endYear + 1)),
    );
  }

  public getMonths(): Observable<KPI.Month[]> {
    return new Observable((observer) => {
      this.translate.get([
        'utils.months.january',
        'utils.months.february',
        'utils.months.march',
        'utils.months.april',
        'utils.months.may',
        'utils.months.june',
        'utils.months.july',
        'utils.months.august',
        'utils.months.september',
        'utils.months.october',
        'utils.months.november',
        'utils.months.december',
      ]).subscribe(monthsTranslations => {
        const months = [
          { nb: 1, name: monthsTranslations['utils.months.january'] },
          { nb: 2, name: monthsTranslations['utils.months.february'] },
          { nb: 3, name: monthsTranslations['utils.months.march'] },
          { nb: 4, name: monthsTranslations['utils.months.april'] },
          { nb: 5, name: monthsTranslations['utils.months.may'] },
          { nb: 6, name: monthsTranslations['utils.months.june'] },
          { nb: 7, name: monthsTranslations['utils.months.july'] },
          { nb: 8, name: monthsTranslations['utils.months.august'] },
          { nb: 9, name: monthsTranslations['utils.months.september'] },
          { nb: 10, name: monthsTranslations['utils.months.october'] },
          { nb: 11, name: monthsTranslations['utils.months.november'] },
          { nb: 12, name: monthsTranslations['utils.months.december'] },
        ];
        observer.next(months);
        observer.complete();
      });
    });
  }

  public getCurrentMonth(): Observable<number> {
    return new Observable((observer) => {
      const currentMonth = new Date().getMonth() + 1;
      observer.next(currentMonth);
      observer.complete();
    });
  }

  public getMonthNumberFromDate(date: Date): number {
    return date.getMonth() + 1;
  }

  public getWeekNumberFromDate(date: Date): number {
    const oneJan = new Date(date.getFullYear(), 0, 1);
    const numberOfDays = Math.floor((date.getTime() - oneJan.getTime()) / (24 * 60 * 60 * 1000));
    const weekNumber = Math.ceil((date.getDay() + 1 + numberOfDays) / 7) - 1;
    return weekNumber;
  }

  public getFormattedDate(): string {
    const now = new Date();
    const pipe = new DatePipe(this.i18n.getCulture());
    return pipe.transform(now, 'd MMM y');
  }
}
